import { ButtonSize, ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import Alert, { AlertVariant } from "@galaxy-io/dls/feedback/Alert";
import { useClipboard } from "@galaxy-io/dls/hooks/useClipboard";
import { InputSize } from "@galaxy-io/dls/inputs/Input";
import TextAreaInput, { TextAreaInputVariant } from "@galaxy-io/dls/inputs/TextAreaInput";
import DescriptionList from "@galaxy-io/dls/lists/DescriptionList";
import Text, { TextSize, TextVariant } from "@galaxy-io/dls/text/Text";
import { FontFamily } from "@galaxy-io/dls/theme/enums";
import { ToastVariant } from "@galaxy-io/dls/toast/Toast";
import { useToast } from "@galaxy-io/dls/toast/useToast";
import { QA_FIELD_LIMIT, type QaEntry, type QaEntryInput } from "@gtm/contracts/core/knowledge";
import {
  CopyIcon,
  EnvelopeSimpleIcon,
  PencilSimpleIcon,
  QuestionIcon,
  TextAlignLeftIcon,
  TrashIcon,
} from "@phosphor-icons/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { z } from "zod";
import { useUrlState } from "@/app/navigation";
import { QueryState } from "@/components/query-state";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ConfirmAction } from "@/components/workspace/confirm-action";
import { DrawerList, DrawerSection, DrawerStack } from "@/components/workspace/drawer-content";
import { PageHeader } from "@/components/workspace/page-header";
import { FeedViewport, Inset } from "@/components/workspace/people-styles";
import { RecordTable } from "@/components/workspace/record-table";
import { SourceMarkdown } from "@/components/workspace/source-markdown";
import { SurfaceDialog } from "@/components/workspace/surface-dialog";
import { BaseToolbar } from "@/components/workspace/toolbar";
import { useDialogReset } from "@/components/workspace/use-dialog-reset";
import { useRetainedValue } from "@/components/workspace/use-retained-value";
import { apiClient, unwrap } from "@/lib/api-client";
import { toastDefaults } from "@/lib/toast";
import { date } from "@/lib/utils";

const EMPTY: QaEntryInput = { question: "", answer: "", source: "" };
const QUERY_KEY = ["knowledge-qa"];

/**
 * A one-line heading for a pasted question. An email reply usually opens with
 * a greeting, so the first line that actually asks something wins; otherwise
 * the first line with content.
 */
function firstLine(text: string) {
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  return lines.find((line) => line.includes("?")) || lines[0] || text.trim();
}

export function QaView() {
  const { params, update } = useUrlState();
  const clipboard = useClipboard();

  const { toast } = useToast();
  const client = useQueryClient();
  const [draft, setDraft] = useState<QaEntryInput | null>(null);
  const [removing, setRemoving] = useState<QaEntry | null>(null);
  const search = params.get("q") || "";
  const selected = z.uuid().safeParse(params.get("qa")).data;
  const editing = params.get("qaEdit") === "1";
  const entries = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => unwrap(apiClient().GET("/knowledge/qa")),
  });
  const currentEntry = entries.data?.items.find((item) => item.id === selected);
  const entry = useRetainedValue(currentEntry);
  const retainedDraft = useRetainedValue(draft);
  const term = search.trim().toLowerCase();
  const items = (entries.data?.items || []).filter((item) =>
    [item.question, item.answer, item.source].some((value) => value.toLowerCase().includes(term)),
  );

  async function copyAnswer(item: QaEntry) {
    if (await clipboard.copy(item.answer)) {
      toast({
        variant: ToastVariant.SUCCESS,
        header: "Answer copied",
        description: "Ready to paste.",
        ...toastDefaults(ToastVariant.SUCCESS),
      });
    } else {
      toast({
        variant: ToastVariant.ERROR,
        header: "Could not copy answer",
        description: "Select the answer text and copy it manually.",
        ...toastDefaults(ToastVariant.ERROR),
      });
    }
  }
  async function remove(item: QaEntry) {
    await unwrap(apiClient().DELETE("/knowledge/qa/{id}", { params: { path: { id: item.id } } }));
    await client.invalidateQueries({ queryKey: QUERY_KEY });
    setRemoving(null);
    update({ qa: null, qaEdit: null });
    toast({
      variant: ToastVariant.SUCCESS,
      header: "Q&A deleted",
      description: firstLine(item.question),
      ...toastDefaults(ToastVariant.SUCCESS),
    });
  }
  return (
    <>
      <PageHeader
        title="Q&A"
        actions={
          <Button label="New Q&A" onClick={() => setDraft(EMPTY)} size={ButtonSize.MEDIUM} />
        }
      />
      <BaseToolbar
        leadingActions={
          <Input
            aria-label="Search questions"
            placeholder="Search questions and answers…"
            value={search}
            onChange={(event) => update({ q: event || null }, true)}
            type="search"
            size={InputSize.SMALL}
            label="Search questions"
            hideLabel
          />
        }
      />
      <FeedViewport>
        <RecordTable
          label="Questions and answers"
          isLoading={entries.isPending}
          error={entries.error || undefined}
          onRetry={() => void entries.refetch()}
          rowKeys={items.map((item) => item.id)}
          onRowClick={(id) => update({ qa: id, qaEdit: null })}
          columns={[
            { key: "question", label: "Question", width: 360, pin: "left" },
            { key: "answer", label: "Answer", width: "auto" },
            { key: "editor", label: "Updated by", width: 150 },
            { key: "updated", label: "Updated", width: 190 },
          ]}
          rows={items.map((item) => ({
            question: (
              <Text size={TextSize.BODY_SM} lineClamp={1}>
                {firstLine(item.question)}
              </Text>
            ),
            answer: (
              <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY} lineClamp={1}>
                {firstLine(item.answer)}
              </Text>
            ),
            editor: item.updatedByName || "—",
            updated: date(item.updatedAt, true),
          }))}
        />
      </FeedViewport>
      {retainedDraft && (
        <QaEditor
          entry={retainedDraft}
          open={!!draft}
          onClose={() => setDraft(null)}
          onSaved={(id) => {
            setDraft(null);
            update({ qa: id, qaEdit: null });
          }}
        />
      )}
      {/* Loading and missing records have their own controlled overlay. */}
      {
        <SurfaceDialog
          open={!!selected && !currentEntry}
          drawer
          title="Q&A"
          onClose={() => update({ qa: null, qaEdit: null })}
        >
          <QueryState
            error={entries.error ?? (entries.isPending ? null : "Q&A entry not found.")}
            loadingMessage="Loading…"
            onRetry={() => void entries.refetch()}
          />
        </SurfaceDialog>
      }
      {entry && (
        <>
          <SurfaceDialog
            open={!!selected && !!currentEntry}
            drawer
            flush
            title={firstLine(entry.question)}
            onClose={() => update({ qa: null, qaEdit: null })}
            headerActions={
              <>
                <Button
                  ariaLabel="Copy answer"
                  tooltip="Copy answer"
                  icon={CopyIcon}
                  size={ButtonSize.SMALL}
                  variant={ButtonVariant.TERTIARY}
                  onClick={() => void copyAnswer(entry)}
                />
                <Button
                  ariaLabel="Edit"
                  tooltip="Edit"
                  icon={PencilSimpleIcon}
                  size={ButtonSize.SMALL}
                  variant={ButtonVariant.TERTIARY}
                  onClick={() => update({ qaEdit: "1" })}
                />
                <Button
                  ariaLabel="Delete"
                  tooltip="Delete"
                  icon={TrashIcon}
                  size={ButtonSize.SMALL}
                  variant={ButtonVariant.TERTIARY}
                  onClick={() => setRemoving(entry)}
                />
              </>
            }
          >
            <DrawerStack>
              <DrawerList>
                <DescriptionList
                  items={[
                    { label: "Sharing", value: "Everyone in this workspace" },
                    { label: "Added", value: date(entry.createdAt, true), family: FontFamily.MONO },
                    {
                      label: "Updated",
                      value: date(entry.updatedAt, true),
                      family: FontFamily.MONO,
                    },
                    { label: "Updated by", value: entry.updatedByName || "—" },
                  ]}
                />
              </DrawerList>
              <DrawerSection header="Question" icon={QuestionIcon} defaultIsOpen>
                <Inset>
                  <SourceMarkdown content={entry.question} breaks />
                </Inset>
              </DrawerSection>
              <DrawerSection header="Answer" icon={TextAlignLeftIcon} defaultIsOpen>
                <Inset>
                  <SourceMarkdown content={entry.answer} breaks />
                </Inset>
              </DrawerSection>
              {entry.source && (
                <DrawerSection header="Source" icon={EnvelopeSimpleIcon}>
                  <Inset>
                    <SourceMarkdown content={entry.source} breaks />
                  </Inset>
                </DrawerSection>
              )}
            </DrawerStack>
          </SurfaceDialog>
          {
            <QaEditor
              open={!!selected && editing}
              key={entry.id}
              id={entry.id}
              entry={{ question: entry.question, answer: entry.answer, source: entry.source }}
              onClose={() => update({ qaEdit: null })}
            />
          }
        </>
      )}
      <ConfirmAction
        isOpen={!!removing}
        header="Delete Q&A?"
        description={
          <>
            “{removing ? firstLine(removing.question) : ""}” and its answer will no longer appear
            here for anyone in this workspace.
          </>
        }
        label="Delete Q&A"
        isDestructive
        onOpenChange={(next) => {
          if (!next) (() => setRemoving(null))();
        }}
        onConfirm={async () => removing && remove(removing)}
      />
    </>
  );
}

function QaEditor({
  open,
  id,
  entry,
  onClose,
  onSaved,
}: {
  open: boolean;
  id?: string;
  entry: QaEntryInput;
  onClose: () => void;
  onSaved?: (id: string) => void;
}) {
  const [formError, setFormError] = useState<string | undefined>();
  const client = useQueryClient();
  const { toast } = useToast();
  const [question, setQuestion] = useState(entry.question);
  const [answer, setAnswer] = useState(entry.answer);
  const [source, setSource] = useState(entry.source);
  const [busy, setBusy] = useState(false);
  const changed = question !== entry.question || answer !== entry.answer || source !== entry.source;
  const tooLong = [question, answer, source].some((value) => value.length > QA_FIELD_LIMIT);
  const complete = !!question.trim() && !!answer.trim();
  useDialogReset(open, () => {
    setFormError(undefined);
    setQuestion(entry.question);
    setAnswer(entry.answer);
    setSource(entry.source);
    setBusy(false);
  });
  async function save() {
    setFormError(undefined);
    setBusy(true);
    try {
      const body = { question, answer, source };
      let savedId = id;
      if (id) {
        await unwrap(apiClient().PUT("/knowledge/qa/{id}", { params: { path: { id } }, body }));
      } else {
        savedId = (await unwrap(apiClient().POST("/knowledge/qa", { body }))).id;
      }
      await client.invalidateQueries({ queryKey: QUERY_KEY });
      toast({
        variant: ToastVariant.SUCCESS,
        header: "Q&A saved",
        description: "Available to everyone in this workspace.",
        ...toastDefaults(ToastVariant.SUCCESS),
      });
      if (!id && savedId && onSaved) onSaved(savedId);
      else onClose();
    } catch (e) {
      setFormError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <SurfaceDialog
      open={open}
      flush
      title={id ? "Edit Q&A" : "New Q&A"}
      headerDescription="Keep a question and the answer you gave, so the next time it comes up the answer is ready."
      onClose={onClose}
      busy={busy}
      dirty={changed || (!id && !!(question || answer || source))}
      footer={
        <>
          <Button
            label="Cancel"
            variant={ButtonVariant.SECONDARY}
            isDisabled={busy}
            onClick={onClose}
          />
          <Button
            label="Save Q&A"
            isLoading={busy}
            isDisabled={busy || !complete || tooLong || (!!id && !changed)}
            onClick={() => void save()}
          />
        </>
      }
    >
      {formError && <Alert variant={AlertVariant.ERROR}>{formError}</Alert>}
      <DrawerStack>
        <TextAreaInput
          label="Question"
          value={question}
          onChange={setQuestion}
          minRows={6}
          fillWidth
          variant={TextAreaInputVariant.BASE}
          isDisabled={busy}
          placeholder="Paste the question, or the email reply that asks it…"
        />
        <TextAreaInput
          label="Answer"
          value={answer}
          onChange={setAnswer}
          minRows={12}
          fillWidth
          variant={TextAreaInputVariant.BASE}
          isDisabled={busy}
          placeholder="Paste the answer you gave…"
        />
        <TextAreaInput
          label="Source (optional)"
          value={source}
          onChange={setSource}
          minRows={6}
          fillWidth
          variant={TextAreaInputVariant.BASE}
          isDisabled={busy}
          placeholder="The original email thread or where the question came from…"
        />
        <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY}>
          Shared with everyone in this workspace. Markdown is supported. Up to 20,000 characters per
          field.
        </Text>
      </DrawerStack>
    </SurfaceDialog>
  );
}
